import { useState, useEffect, useCallback, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import SaveBar from "./SaveBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Monitor, Layout, Settings, Eye, EyeOff, RotateCcw, Bookmark, ZoomIn } from "lucide-react";
import { sidebarGroups } from "@/lib/pages";
import { Input } from "@/components/ui/InputDefault";
import { updateUserSettings } from "@/lib/stores/profileStore";
import { fetchUserContext } from "@/lib/stores/unifiedUserStore";
import { hasPageAccess } from "@/lib/utils/permissions";
import { useDevRoleOverride } from "@/lib/utils/devRoleOverride";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { useZoom, ZOOM_PRESETS } from '@/providers/ZoomProvider';
type EnvironmentSettingsSectionProps = Record<string, never>;

interface SidebarCustomization {
  groupId: string;
  groupLabel: string;
  items: {
    id: string;
    label: string;
    visible: boolean;
    requiresGame: boolean;
  }[];
  visible: boolean;
}

interface EnvironmentSettings {
  // Sidebar Customization
  sidebarItems: SidebarCustomization[];

  // Default Project Behavior
  defaultProjectMode: "last-used" | "specific" | "always-ask";
  specificProjectId?: string;
  specificCompanyId?: string;

  // Workspace Preferences
  autoSaveInterval: number; // in minutes
  confirmBeforeLeaving: boolean;
  showWelcomeScreen: boolean;
  rememberWindowSize: boolean;

  // Navigation Preferences
  enableKeyboardShortcuts: boolean;
  showBreadcrumbs: boolean;
}

export default function EnvironmentSettingsSection(_props: EnvironmentSettingsSectionProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { games, companies, selectedGame, selectedCompany, currentUser } = useSimplifiedUserData();
  const { zoom, setZoom } = useZoom();
  
  // Subscribe to dev role override to re-render when role changes
  const overrideRoleId = useDevRoleOverride((state) => state.overrideRoleId);

  // Environment settings state
  const [settings, setSettings] = useState<EnvironmentSettings>({
    sidebarItems: [],
    defaultProjectMode: "last-used",
    specificProjectId: undefined,
    specificCompanyId: undefined,
    autoSaveInterval: 5,
    confirmBeforeLeaving: true,
    showWelcomeScreen: true,
    rememberWindowSize: true,
    enableKeyboardShortcuts: true,
    showBreadcrumbs: true,
  });

  const [originalSettings, setOriginalSettings] = useState<EnvironmentSettings>(settings);

  // Filter sidebar items based on user permissions
  // This memoizes the filtering logic and re-runs when role override or company changes
  const accessibleSidebarGroups = useMemo(() => {
    if (!selectedCompany?.id) return sidebarGroups;
    
    return sidebarGroups.map(group => ({
      ...group,
      items: group.items.filter(item => {
        // Always show settings and logout
        if (item.id === 'settings' || item.id === 'logout') {
          return true;
        }
        
        // Check if user has access to this page
        return hasPageAccess(selectedCompany.id, item.id);
      })
    })).filter(group => group.items.length > 0); // Remove empty groups
  }, [selectedCompany?.id, overrideRoleId]); // Re-run when company or role override changes
  
  const loadEnvironmentSettings = useCallback(async () => {
    try {
      // Initialize sidebar items from the accessible sidebarGroups (filtered by permissions)
      const sidebarCustomization: SidebarCustomization[] = accessibleSidebarGroups.map(group => ({
        groupId: group.id,
        groupLabel: group.label,
        visible: true,
        items: group.items.map(item => ({
          id: item.id,
          label: item.label,
          visible: true,
          requiresGame: item.requiresGame || false
        }))
      }));

      // Use default company, game, and mode from user profile settings
      const defaultCompany = currentUser?.settings?.defaultCompany || selectedCompany?.id;
      const defaultGame = currentUser?.settings?.defaultGame || selectedGame?.id;
      const defaultProjectMode = currentUser?.settings?.defaultProjectMode || "last-used";

      const defaultSettings: EnvironmentSettings = {
        sidebarItems: sidebarCustomization,
        defaultProjectMode: defaultProjectMode,
        specificProjectId: defaultGame,
        specificCompanyId: defaultCompany,
        autoSaveInterval: currentUser?.settings?.autoSaveInterval ?? 5,
        confirmBeforeLeaving: true,
        showWelcomeScreen: true,
        rememberWindowSize: true,
        enableKeyboardShortcuts: true,
        showBreadcrumbs: true,
      };

      // Load saved settings from user profile settings
      const savedSidebarSettings = currentUser?.settings?.sidebarVisibility;
      
      if (savedSidebarSettings) {
        try {
          // Merge saved visibility with default settings
          const mergedSidebarItems = defaultSettings.sidebarItems.map(group => {
            const savedGroup = savedSidebarSettings.find((sg: any) => sg.groupId === group.groupId);
            if (savedGroup) {
              return {
                ...group,
                visible: savedGroup.visible,
                items: group.items.map(item => {
                  const savedItem = savedGroup.items.find((si: any) => si.id === item.id);
                  return savedItem ? { ...item, visible: savedItem.visible } : item;
                })
              };
            }
            return group;
          });
          
          setSettings({ 
            ...defaultSettings, 
            sidebarItems: mergedSidebarItems
          });
        } catch (error) {
          console.error("Error parsing saved sidebar settings:", error);
          setSettings(defaultSettings);
        }
      } else {
        setSettings(defaultSettings);
      }

      setOriginalSettings(JSON.parse(JSON.stringify(defaultSettings)));
    } catch (error) {
      console.error("Failed to load environment settings:", error);
      toast({
        title: "Error",
        description: "Failed to load environment settings",
        variant: "destructive",
      });
    }
  }, [currentUser, selectedCompany, selectedGame, toast, accessibleSidebarGroups]);

  // Initialize settings
  useEffect(() => {
    loadEnvironmentSettings();
  }, [loadEnvironmentSettings]);

  // Check if there are any changes
  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // Handle settings updates
  const updateSettings = (field: keyof EnvironmentSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Toggle sidebar group visibility
  const toggleSidebarGroup = (groupId: string, visible: boolean) => {
    // Prevent hiding user-settings group (contains settings and logout)
    if (groupId === 'user-settings' && !visible) {
      toast({
        title: "Cannot Hide",
        description: "Settings and logout must always remain accessible",
        variant: "destructive",
      });
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      sidebarItems: prev.sidebarItems.map(group =>
        group.groupId === groupId ? { ...group, visible } : group
      )
    }));
  };

  // Toggle sidebar item visibility
  const toggleSidebarItem = (groupId: string, itemId: string, visible: boolean) => {
    // Prevent hiding settings and logout
    if ((itemId === 'settings' || itemId === 'logout') && !visible) {
      toast({
        title: "Cannot Hide",
        description: `${itemId === 'settings' ? 'Settings' : 'Logout'} must always remain accessible`,
        variant: "destructive",
      });
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      sidebarItems: prev.sidebarItems.map(group =>
        group.groupId === groupId
          ? {
            ...group,
            items: group.items.map(item =>
              item.id === itemId ? { ...item, visible } : item
            )
          }
          : group
      )
    }));
  };

  // Reset to defaults
  const resetToDefaults = () => {
    loadEnvironmentSettings();
    toast({
      title: "Settings Reset",
      description: "Environment settings have been reset to defaults",
    });
  };

  // Handle save
  const handleSave = async () => {
    try {
      // Check if sidebar visibility or project settings changed
      const sidebarVisibilityChanged = 
        JSON.stringify(settings.sidebarItems) !== JSON.stringify(originalSettings.sidebarItems);
      
      const projectSettingsChanged = 
        settings.specificCompanyId !== originalSettings.specificCompanyId ||
        settings.specificProjectId !== originalSettings.specificProjectId ||
        settings.defaultProjectMode !== originalSettings.defaultProjectMode;

      const workspaceSettingsChanged = 
        settings.autoSaveInterval !== originalSettings.autoSaveInterval;

      const needsProfileUpdate = sidebarVisibilityChanged || projectSettingsChanged || workspaceSettingsChanged;

      if (needsProfileUpdate) {
        const settingsUpdate: Record<string, any> = {};
        
        // Save sidebar visibility preferences
        if (sidebarVisibilityChanged) {
          settingsUpdate.sidebarVisibility = settings.sidebarItems.map(group => ({
            groupId: group.groupId,
            visible: group.visible,
            items: group.items.map(item => ({
              id: item.id,
              visible: item.visible
            }))
          }));
        }
        
        // Save project settings
        if (projectSettingsChanged) {
          settingsUpdate.defaultCompany = settings.specificCompanyId || "";
          settingsUpdate.defaultGame = settings.specificProjectId || "";
          settingsUpdate.defaultProjectMode = settings.defaultProjectMode;
        }

        // Save workspace settings (auto-save interval)
        if (workspaceSettingsChanged) {
          settingsUpdate.autoSaveInterval = settings.autoSaveInterval;
        }
        
        await updateUserSettings(settingsUpdate, toast);

        // Refresh user context to get updated settings
        await fetchUserContext();
      }

      setOriginalSettings(JSON.parse(JSON.stringify(settings)));

      toast({
        title: "Success",
        description: "Environment settings saved successfully",
      });

    } catch (error) {
      console.error("Error saving environment settings:", error);
      toast({
        title: "Error",
        description: "Failed to save environment settings",
        variant: "destructive",
      });
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
  };

  return (
    <div>
      <div className="max-w-4xl space-y-8">


        {/* Sidebar Customization */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Layout className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.sidebar_customization')}</h3>
            </div>
            <Button variant="outline" size="sm" onClick={resetToDefaults}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>

          <div className="space-y-6">
            {/* Sidebar Items Configuration */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-4">{t('platform.settings.environmentsettingssection.visible_navigation_items')}</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Only items you have access to are shown. Settings and Logout cannot be hidden.
              </p>
              <div className="space-y-4">
                {settings.sidebarItems.map((group) => (
                  <div key={group.groupId} className="border border-input rounded p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <h5 className="font-medium text-muted-foreground">{group.groupLabel}</h5>
                        {!group.visible && (
                          <Badge variant="secondary" className="text-xs">{t('platform.settings.environmentsettingssection.hidden')}</Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">{t('platform.settings.environmentsettingssection.show_group')}</span>
                        <input
                          type="checkbox"
                          checked={group.visible}
                          onChange={(e) => toggleSidebarGroup(group.groupId, e.target.checked)}
                          disabled={group.groupId === 'user-settings'}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title={group.groupId === 'user-settings' ? 'Settings and Logout must remain visible' : ''}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {group.items.map((item) => {
                        const isProtected = item.id === 'settings' || item.id === 'logout';
                        return (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-muted rounded">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-muted-foreground">{item.label}</span>
                              {isProtected && (
                                <Badge variant="outline" className="text-xs">Required</Badge>
                              )}
                            </div>
                            <input
                              type="checkbox"
                              checked={item.visible && group.visible}
                              disabled={!group.visible || isProtected}
                              onChange={(e) => toggleSidebarItem(group.groupId, item.id, e.target.checked)}
                              className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-input rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title={isProtected ? 'This item must remain visible' : ''}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Default Project Behavior */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Bookmark className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.default_project_behavior')}</h3>
          </div>

          <div className="space-y-4 ">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-3">
                When opening the application:
              </label>
              <div className="space-y-4 flex flex-col ">
                <div className="flex items-center">
                  <Input
                    id="last-used"
                    type="radio"
                    name="defaultProjectMode"
                    value="last-used"
                    variant="xs"
                    checked={settings.defaultProjectMode === "last-used"}
                    onChange={(e) => updateSettings('defaultProjectMode', e.target.value)}
                  />
                  <label htmlFor="last-used" className="ml-2 text-sm text-muted-foreground">
                    <span className="font-medium">{t('platform.settings.environmentsettingssection.open_last_used_project')}</span>
                    <span className="block text-muted-foreground">{t('platform.settings.environmentsettingssection.resume_where_you_left_off')}</span>
                  </label>
                </div>

                <div className="flex items-center">
                  <Input
                    id="specific"
                    type="radio"
                    name="defaultProjectMode"
                    value="specific"
                    variant="xs"
                    checked={settings.defaultProjectMode === "specific"}
                    onChange={(e) => updateSettings('defaultProjectMode', e.target.value)}
                  />
                  <label htmlFor="specific" className="ml-2 text-sm text-muted-foreground">
                    <span className="font-medium">{t('platform.settings.environmentsettingssection.open_specific_project')}</span>
                    <span className="block text-muted-foreground">{t('platform.settings.environmentsettingssection.always_start_with_the_same_project')}</span>
                  </label>
                </div>




              </div>
            </div>

            {/* Specific Project Selection */}
            {settings.defaultProjectMode === "specific" && (
              <div className="bg-muted p-4 rounded space-y-4">
                <div>
                  <label htmlFor="default-company" className="block text-sm font-medium text-muted-foreground mb-2">
                    Default Company
                  </label>
                  <select
                    id="default-company"
                    value={settings.specificCompanyId || ""}
                    onChange={(e) => updateSettings('specificCompanyId', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded bg-surface focus:outline-none focus focus"
                  >
                    <option value="">{t('platform.settings.environmentsettingssection.select_a_company')}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="default-game" className="block text-sm font-medium text-muted-foreground mb-2">
                    Default Game/Project
                  </label>
                  <select
                    id="default-game"
                    value={settings.specificProjectId || ""}
                    onChange={(e) => updateSettings('specificProjectId', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded bg-surface focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    disabled={!settings.specificCompanyId}
                  >
                    <option value="">{t('platform.settings.environmentsettingssection.select_a_project')}</option>
                    {games
                      .filter(game => !settings.specificCompanyId || game.company_id === settings.specificCompanyId)
                      .map((game) => (
                        <option key={game.id} value={game.id}>
                          {game.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workspace Preferences */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Monitor className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.workspace_preferences')}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Auto-save Interval */}
            <div>
              <label htmlFor="autosave-interval" className="block text-sm font-medium bg-background text-muted-foreground mb-2">
                Auto-save Interval (minutes)
              </label>
              <select
                id="autosave-interval"
                value={settings.autoSaveInterval}
                onChange={(e) => updateSettings('autoSaveInterval', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-input rounded  focus:outline-none bg-background focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>1 minute</option>
                <option value={2}>2 minutes</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={0}>{t('platform.settings.environmentsettingssection.disabled')}</option>
              </select>
            </div>

            {/* Other Workspace Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.confirm_before_leaving')}</h4>
                  <p className="text-sm text-muted-foreground">{t('platform.settings.environmentsettingssection.ask_before_closing_with_unsaved_changes')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.confirmBeforeLeaving}
                  onChange={(e) => updateSettings('confirmBeforeLeaving', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.show_welcome_screen')}</h4>
                  <p className="text-sm text-muted-foreground">{t('platform.settings.environmentsettingssection.display_welcome_screen_on_startup')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.showWelcomeScreen}
                  onChange={(e) => updateSettings('showWelcomeScreen', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
                />
              </div>


            </div>
          </div>
        </div>

        {/* Navigation Preferences */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <Settings className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.navigation_ui_preferences')}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.keyboard_shortcuts')}</h4>
                <p className="text-sm text-muted-foreground">{t('platform.settings.environmentsettingssection.enable_keyboard_navigation_shortcuts')}</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableKeyboardShortcuts}
                onChange={(e) => updateSettings('enableKeyboardShortcuts', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">{t('platform.settings.environmentsettingssection.show_breadcrumbs')}</h4>
                <p className="text-sm text-muted-foreground">{t('platform.settings.environmentsettingssection.display_navigation_breadcrumbs')}</p>
              </div>
              <input
                type="checkbox"
                checked={settings.showBreadcrumbs}
                onChange={(e) => updateSettings('showBreadcrumbs', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-input rounded"
              />
            </div>
          </div>
        </div>

        {/* UI Zoom */}
        <div className="bg-background p-6 rounded border">
          <div className="flex items-center space-x-2 mb-4">
            <ZoomIn className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-medium text-muted-foreground">UI Zoom</h3>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {ZOOM_PRESETS.map((preset) => (
              <Button 
                key={preset}
                variant={zoom === preset ? "default" : "outline"}
                size="sm"
                onClick={() => setZoom(preset)}
              >
                {Math.round(preset * 100)}%
              </Button>
            ))}
          </div>
        </div>

        {/* Current Settings Summary */}
        <div className="bg-muted p-4 rounded border border-input bg-background border">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('platform.settings.environmentsettingssection.current_configuration_summary')}</h4>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <span className="font-medium">{t('platform.settings.environmentsettingssection.sidebar')}</span> {settings.sidebarItems.filter(g => g.visible).length} groups, {settings.sidebarItems.flatMap(g => g.items).filter(i => i.visible).length} total items visible
            </p>
            <p>
              <span className="font-medium">{t('platform.settings.environmentsettingssection.default_project')}</span> {
                settings.defaultProjectMode === "last-used" ? "Resume last used project" :
                  settings.defaultProjectMode === "specific" ? `Open ${games.find(g => g.id === settings.specificProjectId)?.name || "selected project"}` :
                    "Always ask"
              }
            </p>
            <p>
              <span className="font-medium">{t('platform.settings.environmentsettingssection.auto_save')}</span> {settings.autoSaveInterval === 0 ? "Disabled" : `Every ${settings.autoSaveInterval} minutes`}
            </p>
          </div>
        </div>
      </div>

      <SaveBar
        isVisible={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
        hasChanges={hasChanges}
      />
    </div>
  );
}
